/*@flow*/
import assert from './assert';
import isFunction from './isFunction';
import isArray from './isArray';
import mixin from './mixin';
import isStruct from './isStruct';
import isObject from './isObject';
import refinement from './refinement';
import decompose from './decompose';
import compose from 'lib/compose';
import type { CombinatorT, TypeT } from './types';

function validate1(combinator, mixins) {
  if (process.env.NODE_ENV !== 'production') {
    assert(isFunction(combinator), function() {
      return 'Invalid argument combinator supplied to extend(combinator, mixins, options), expected a function';
    });
    assert(isArray(mixins), function() {
      return 'Invalid argument mixins supplied to extend(combinator, mixins, options), expected an array';
    });
  }
}

function validate2(unrefinedType, key) {
  if (process.env.NODE_ENV !== 'production') {
    assert(isObject(unrefinedType) || isStruct(unrefinedType), function() {
      return (
        'Invalid argument mixins[' +
        key +
        '] supplied to processMixins(mixins), expected an object, struct, interface or a refinement (of struct or interface)'
      );
    });
  }
}

type PropT = { [key: string]: TypeT<*> };
type MixinT = TypeT<*> | PropT;

function processMixins(mixins: MixinT[]) {
  let props = {};
  let metaOpts = {};
  let prototype = {};
  let predicates = [];

  mixins.forEach((x, key) => {
    const { unrefinedType, predicates: preds } = decompose(x);
    validate2(unrefinedType, key);
    predicates = predicates.concat(preds);
    mixin(props, getProps(unrefinedType));
    mixin(metaOpts, getMetaOpts(unrefinedType));
    mixin(prototype, unrefinedType.prototype);
  });

  return {
    props,
    metaOpts,
    prototype,
    predicates,
  };
}

function getProps(type) {
  return isObject(type) ? type : type.meta.props;
}

function getMetaOpts(type) {
  return isObject(type) ? {} : type.meta.options;
}

function getName(combinator, options) {
  const fn = combinator.getOptions;
  return fn ? fn(options).name : null;
}

function getResult(combinator, mixins) {
  const { props, metaOpts, prototype, predicates } = processMixins(mixins);
  const unrefinedType = combinator(props);
  const fns = predicates.map(pred => type => refinement(type, pred));
  const refine = compose(...fns);
  let result = refine(unrefinedType);
  mixin(result.prototype, prototype);
  return result.opt(metaOpts);
}

function setName(result, combinator, options) {
  const name = getName(combinator, options);
  if (name) {
    result.displayName = name;
    result.meta.name = name;
  }
}

export default function extend(
  combinator: CombinatorT,
  mixins: MixinT[],
  options?: string | {} | void
): TypeT<*> {
  validate1(combinator, mixins);
  let result = getResult(combinator, mixins);
  setName(result, combinator, options);
  return result;
}
